'use strict';

import React from 'react';
import _ from 'underscore';
import StampStore from '../stores/StampStore';
import StampActionCreators from '../actions/StampActionCreators';
import PageControlsTypes from '../constants/PageControlsTypes';
import LightboxStore from '../stores/LightboxStore';
import NavigationContextTypes from '../constants/NavigationContextTypes';
import Stamp from '../stamp/Stamp';
import Loader from '../atoms/Loader';
import Container from '../organisms/Container';
import OffsetMonitor from '../atoms/OffsetMonitor';
import Group from '../organisms/Group';
import TrendingNextUp from '../organisms/TrendingNextUp';
import ScrollStateMixin from '../mixins/ScrollStateMixin';
import ScreenSizeMixin from '../mixins/ScreenSizeMixin';
import PureRenderMixin from '../mixins/PureRenderMixin';
import invariant from 'react/lib/invariant';
import shallowEqual from 'react/lib/shallowEqual';
import { pascalize } from 'humps';
import waitForElasticScroll from '../utils/waitForElasticScroll';
import { isMobile } from '../utils/Browser';
import DocumentTitle from '../atoms/DocumentTitle';
import getStampCountPerRow from '../utils/getStampCountPerRow';
import StampPageMixins from '../mixins/StampPageMixins';
import delay from '../utils/delay';
import AppContext from '../AppContext';
import { PropTypes } from 'react';
import parseStampSegment from '../utils/parseStampSegment';

var MIN_NEXT_STAMP_COUNT = 3,
    MAX_PREFETCH_COUNT = 3;

/**
 * Creates a stamp page handler for route.
 * Expects StampPageMixin to provide `getPage`, `requestPage`.
 */
function createStampPage({ routeName, stampPageMixin }) {
  invariant(stampPageMixin, 'stampPageMixin is required');

  var StampPage = React.createClass({
    displayName: pascalize(routeName),

    mixins: [stampPageMixin, ScrollStateMixin, ScreenSizeMixin, PureRenderMixin],

    propTypes: {
      params: PropTypes.shape({
        stampSegment: PropTypes.string.isRequired
      }).isRequired
    },

    statics: {
      willTransitionTo(transition, params, query, callback) {

        // If user scrolls past the viewport and we replace page content before
        // the scroll fully "returns", scroll position may get messed up in WebKit.

        // We will attempt to wait for elastic scroll,
        // but carry on if it doesn't happen soon enough.

        if (isMobile()) {
          return callback();
        }

        Promise.race([
          waitForElasticScroll(),
          delay(2000)
        ]).then(callback);
      }
    },

    getInitialState() {
      return this.getLightboxState();
    },

    parseStampId(props) {
      props = props || this.props;
      const { stampId } = parseStampSegment(props.params.stampSegment);
      return stampId;
    },

    getScrollState(scrollY, previousScrollY) {
      return {
        isScrolled: scrollY > 52,
        isScrollingDown: scrollY > 52 && previousScrollY < scrollY,
        hasScrolledToInfo: this.hasScrolledToOffset(this._infoPanelOffsetTop),
        hasScrolledToNextUp: this.hasScrolledToOffset(this._nextUpOffsetTop),
        hasScrolledPastNextUp: this.hasScrolledPastOffset(this._nextUpOffsetTop)
      };
    },

    getLightboxState() {
      return {
        lightboxImageUuid: LightboxStore.getActiveImageUuid(),
        lightboxSectionType: LightboxStore.getActiveSectionType()
      };
    },

    getStateFromStores(props) {
      var stamps = this.getPage(),
          stampId = this.parseStampId(props),
          index = stamps.map(s => s.id).indexOf(stampId),
          stamp = StampStore.get(stampId),
          isLastPage = this.isLastPage(),
          perFirstPage = this.getPerFirstPageCount(),
          needsNextPage = !isLastPage && (stamps.length - index <= perFirstPage);

      return {
        stamp: stamp,
        primaryZine: stamp && this.getStampPrimaryZine(stamp.id),
        nextStamps: stamps.slice(index + 1),
        nextUpTitle: this.getNextUpTitle(),
        needsNextPage: needsNextPage,
        isExpectingPage: this.isExpectingPage(),
        isLastPage: isLastPage,
        routeParams: _.omit(props.params, 'stampSegment')
      };
    },

    getPerFirstPageCount() {
      return Math.max(
        MIN_NEXT_STAMP_COUNT,
        getStampCountPerRow(this.getScreenSize(), MIN_NEXT_STAMP_COUNT)
      );
    },

    getNextUpStampItemProps(stamp) {
      var isTrending = false,
          primaryZine = null;

      if (this.shouldShowPrimaryZineInNextUp()) {
        primaryZine = this.getStampPrimaryZine(stamp.id);
      }

      return {
        isTrending: isTrending,
        primaryZine: primaryZine
      };
    },

    getRouteProps() {
      return {
        routeName: routeName,
        routeParams: this.state.routeParams
      };
    },

    hasEnoughStampsForNextUp() {
      var { nextStamps, isExpectingPage, isLastPage } = this.state;
      return nextStamps.length || !isLastPage || isExpectingPage;
    },

    getNavigationContext() {
      const { stamp, primaryZine } = this.state;
      if (!stamp) {
        return null;
      }

      const { id: stampId } = stamp;
      const {
        isScrolled,
        isScrollingDown,
        hasScrolledToInfo,
        hasScrolledPastNextUp
      } = this.state;

      let { nextUpTitle } = this.state;
      if (!this.hasEnoughStampsForNextUp()) {
        // Special case: we'll render <TrendingNextUp /> instead so title should match it
        nextUpTitle = StampPageMixins.Feed.getNextUpTitle.call(this);
      }

      return [NavigationContextTypes.STAMP, {
        stampId,
        isScrolled,
        isScrollingDown,
        hasScrolledToInfo,
        hasScrolledPastNextUp,
        nextUpTitle: nextUpTitle,
        primaryZineSlug: primaryZine && primaryZine.slug
      }];
    },

    getPageControls() {
      const { stamp } = this.state;
      if (!stamp) {
        return null;
      }

      const { id: stampId } = stamp;
      const {
        hasScrolledToNextUp,
        hasScrolledToInfo,
        lightboxImageUuid: imageUuid,
        lightboxSectionType: sectionType
      } = this.state;

      const pendingZine = this.getStampPendingZine(stampId);

      if (imageUuid) {
        return [PageControlsTypes.IMAGE, { stampId, imageUuid, sectionType }];
      } else if (pendingZine && !hasScrolledToNextUp) {
        const { slug: zineSlug } = pendingZine;
        return [PageControlsTypes.STAMP_REVIEW, { stampId, zineSlug }];
      } else if (!hasScrolledToInfo) {
        return [PageControlsTypes.STAMP, { stampId }];
      }
    },

    getAppContext() {
      const [navigationContextType, navigationContextOptions] = this.getNavigationContext() || [];
      const [pageControlsType, pageControlsOptions] = this.getPageControls() || [];
      const { stamp: activeStamp } = this.state;

      return {
        navigationContextType,
        navigationContextOptions,
        pageControlsType,
        pageControlsOptions,
        activeStamp
      };
    },

    componentWillMount() {
      this.resetRecordedOffsets();
    },

    componentDidMount() {
      this.requestPage(true);
      this.handleStampIdChange(this.props, false);
      this.preloadNextStamps();

      LightboxStore.addChangeListener(this.handleLightboxChanged);
    },

    componentWillReceiveProps(nextProps) {
      if (!shallowEqual(this.props.params, nextProps.params)) {
        this.setState(this.getStateFromStores(nextProps));
        this.handleStampIdChange(nextProps, true);
        this.resetRecordedOffsets();
      }
    },

    componentDidUpdate(prevProps, prevState) {
      if (this.state.needsNextPage && !this.state.isExpectingPage) {
        this.requestPage();
      }

      var prevStamp = prevState.stamp,
          stamp = this.state.stamp;

      if (prevStamp === stamp) {
        return;
      }

      if (!prevStamp || !stamp || prevStamp.id !== stamp.id) {
        this.preloadNextStamps();
      }
    },

    resetRecordedOffsets() {
      this._infoPanelOffsetTop = 0;
      this._nextUpOffsetTop = 0;
    },

    handleLightboxChanged() {
      this.setState(this.getLightboxState());
    },

    handleStampIdChange(props, isNextStamp) {
      var { stampSegment } = props.params;
      StampActionCreators.requestStamp(stampSegment, ['content', 'zines', 'images']);
      StampActionCreators.incrementStampViews(stampSegment, isNextStamp);
    },

    preloadNextStamps() {
      if (!this.state.nextStamps) {
        return;
      }

      var count = Math.min(MAX_PREFETCH_COUNT, this.getPerFirstPageCount());
      this.state.nextStamps.slice(0, count).forEach(nextStamp =>
        StampActionCreators.requestStamp(nextStamp.id, ['content', 'zines'])
      );
    },

    componentWillUnmount() {
      LightboxStore.removeChangeListener(this.handleLightboxChanged);
    },

    render() {
      var stamp = this.state.stamp;
      if (!stamp) {
        return <Loader absoluteCenter />;
      }

      return (
        <AppContext {...this.getAppContext()}>
          <DocumentTitle title={stamp.title}>
            <Stamp stamp={stamp}
                   primaryZine={this.state.primaryZine}
                   nextUp={this.renderNextUp()}
                   onInfoPanelOffsetTopChange={this.handleInfoPanelOffsetTopChange}
                   isInViewer />
          </DocumentTitle>
        </AppContext>
      );
    },

    renderNextUp() {
      if (!this.hasEnoughStampsForNextUp()) {
        return this.renderTrendingNextUp();
      }

      var { nextStamps, primaryZine, nextUpTitle, isExpectingPage, isLastPage } = this.state,
          colors = this.getColors(primaryZine);

      return (
        <OffsetMonitor onOffsetTopChange={this.handleNextUpOffsetTopChange}>
          <Container key={'next-' + this.parseStampId()}
                     context='Stamp'
                     style={{backgroundColor: `#${colors.background}` }}
                     color={colors.text}>
            <Group type='stamps'
                   mode='grid'
                   columns={MIN_NEXT_STAMP_COUNT}
                   context='page'
                   color={colors.text}
                   title={nextUpTitle}
                   items={nextStamps}
                   itemProps={this.getRouteProps()}
                   getItemProps={this.getNextUpStampItemProps}
                   isExpectingPage={isExpectingPage}
                   isLastPage={isLastPage}
                   loadNextPage={this.requestPage}
                   isInvalidated={false}
                   perFirstPage={this.getPerFirstPageCount()} />
          </Container>
        </OffsetMonitor>
      );
    },

    renderTrendingNextUp() {
      return (
        <OffsetMonitor onOffsetTopChange={this.handleNextUpOffsetTopChange}>
          <Container context='Stamp'>
            <TrendingNextUp perFirstPage={this.getPerFirstPageCount()}
                            itemProps={this.getRouteProps()}
                            getItemProps={this.getNextUpStampItemProps} />
          </Container>
        </OffsetMonitor>
      );
    },

    handleNextUpOffsetTopChange(nextUpOffsetTop) {
      this._nextUpOffsetTop = nextUpOffsetTop;
      this.updateScrollState();
    },

    handleInfoPanelOffsetTopChange(infoPanelOffsetTop) {
      this._infoPanelOffsetTop = infoPanelOffsetTop;
      this.updateScrollState();
    }
  });

  if (module.makeHot) {
    StampPage = module.makeHot(StampPage, routeName);
  }

  return StampPage;
}

module.exports = createStampPage;



/** WEBPACK FOOTER **
 ** ./pages/createStampPage.js
 **/